import { Box, Stack } from "@mui/material";
import { Custom, FormGuest, FormSignIn, Workflow } from "components";
import { Utils } from "components/workflow";
import { useApp, useService, useSession, useTheme } from "contexts";
import { useWorkflow } from "contexts/components";
import { Auxiliars } from "helpers";
import { IUser } from "interfaces";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { SessionType } from "types";
import { Enums } from "utils";
import { createStep } from "utils/helpers";

const StepGuest = forwardRef((_, ref) => {
  const { current, data, updateData, pushFlow } =
    useWorkflow<Workflow.Utils.PotluckType>();
  const { isAuthenticated, login, user } = useSession();
  const { userService } = useService();
  const { theme } = useTheme();
  const { t } = useApp();

  const [userAuthentication, setUserAuthentication] = useState<boolean>(false);

  useEffect(() => {
    if (isAuthenticated) return;
    updateData(
      "user",
      Auxiliars.walkAndReplace<IUser>(data.user, [
        {
          path: "_id",
          value: user?.id || Auxiliars.generateObjectId(),
        },
        {
          path: "role",
          value: isAuthenticated ? "user" : "guest",
        },
        {
          path: "password",
          value: "",
        },
      ])
    );
  }, [current.step]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      let isValid = true;
      let updatedData = null;

      if (userAuthentication) {
        const response = await userService.findByEmailPassword(
          data.user.email,
          data.user.password
        );

        if (response) {
          // TODO : improve it
          login({
            id: response._id,
            name: response.full_name,
            email: response.email,
            role: response.role,
            settings: response.settings,
          } as SessionType);

          updatedData = await updateData("user", response);
        } else {
          isValid = false;
        }
      }

      return {
        isValid,
        data: updatedData,
        message: isValid
          ? ""
          : t.component.workflow.shared.guest.validation.signed_in,
      };
    },
  }));

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.name === "email") handleCheckEmail(event.target.value);
  };

  const hanldeOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateData("user." + event.target.name, event.target.value);
  };

  const handleCheckEmail = async (email: string) => {
    const response = await userService.isEmailInUse(email);
    setUserAuthentication(response);
  };

  const handleOnSwitch = () => {
    setUserAuthentication(() => {
      updateData("user.email", "");
      return !userAuthentication;
    });
  };

  const handleOnboardingClick = () => {
    // Push the onboarding flow when the button is clicked
    pushFlow({
      name: Enums.EnumFlow.Onboarding,
      count: Utils.OnboardingSteps.length,
      end: Utils.OnboardingSteps.length,
      step: 1,
    });
  };

  return (
    <>
      <Custom.Typography size={theme.font.md} weight={theme.font.bold}>
        {userAuthentication
          ? t.component.workflow.shared.user.header
          : t.component.workflow.shared.guest.header}
      </Custom.Typography>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.xs}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {userAuthentication
              ? t.component.workflow.shared.user.user_details
              : t.component.workflow.shared.guest.user_details}
          </Custom.Typography>
          <Custom.Typography
            size={theme.font.xs}
            color={theme.color.font.accent}
          >
            {userAuthentication
              ? t.component.workflow.shared.user.details_confirmation
              : t.component.workflow.shared.guest.details_confirmation}
          </Custom.Typography>
        </Stack>
      </Box>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          {!userAuthentication && (
            <FormGuest.Fields
              current={{
                email: data.user.email,
                first_name: data.user.first_name,
              }}
              onChange={hanldeOnChange}
              onBlur={handleOnBlur}
            />
          )}
          {userAuthentication && (
            <FormSignIn.Fields
              current={{
                email: data.user.email,
                password: data.user.password,
              }}
              onChange={hanldeOnChange}
              onBlur={handleOnBlur}
            />
          )}
        </Stack>
      </Box>

      {!isAuthenticated && (
        <Box mt={theme.spacing.md}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={theme.spacing.sm}
          >
            <Custom.Button size="small" onClick={handleOnSwitch}>
              {userAuthentication
                ? t.component.workflow.shared.guest.create_account
                : t.component.workflow.shared.guest.has_account}
            </Custom.Button>
            <Custom.Typography>{t.conjunction.or}</Custom.Typography>
            <Custom.Button size="small" onClick={handleOnboardingClick}>
              {t.action.sign_up}
            </Custom.Button>
          </Stack>
        </Box>
      )}
    </>
  );
});

export default createStep<Workflow.Utils.PotluckType>(StepGuest);
